import { useApolloClient } from '@apollo/client';
import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import {
  ToastAlertVariant,
  ToastVariant
} from '@aurora/shared-client/components/common/ToastAlert/enums';
import type ToastProps from '@aurora/shared-client/components/common/ToastAlert/ToastAlertProps';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useToasts from '@aurora/shared-client/components/context/ToastContext/useToasts';
import useAuthFlow from '@aurora/shared-client/components/useAuthFlow';
import useFrameEnd from '@aurora/shared-client/components/context/AnalyticsParentFrames/useFrameEnd';
import ParentFrameSupplier from '@aurora/shared-utils/analytics/ParentFrameSupplier';
import { hasErrors } from '@aurora/shared-client/helpers/apollo/ApolloHelper';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import useMutationWithTracing from '@aurora/shared-client/components/useMutationWithTracing';
import useRegistrationStatus from '@aurora/shared-client/components/users/useRegistrationStatus';
import type IconTypes from '@aurora/shared-client/icons';
import Icons from '@aurora/shared-client/icons';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import { AuthFlow } from '@aurora/shared-client/types/enums';
import { RsvpResponse } from '@aurora/shared-generated/types/graphql-schema-types';
import { LocalStorageKeys } from '@aurora/shared-types/community/enums';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React, { useCallback, useContext, useEffect, useId, useState } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { useUIDSeed } from 'react-uid';
import { useLocalStorage } from 'react-use';
import type { AnonymousUserAction } from '../../../types';
import { AnonAction } from '../../../types/enums';
import type {
  CreateRsvpResponseMutation,
  CreateRsvpResponseMutationVariables,
  DeleteRsvpResponseMutation,
  DeleteRsvpResponseMutationVariables
} from '../../../types/graphql-types';
import contextMessageQuery from '../../context/ContextMessage.query.graphql';
import useTranslation from '../../useTranslation';
import createRsvpResponseMutation from './CreateRsvpResponse.mutation.graphql';
import deleteRsvpResponseMutation from './DeleteRsvpResponse.mutation.graphql';
import localStyles from './OccasionRsvpStatusForCurrentUser.module.pcss';

interface Props {
  /**
   * The message for which rsvp has to be displayed in the published view.
   */
  occasionId: string;
  /**
   * RSVP response for which this component renders appropriate actions.
   */
  rsvpStatus: RsvpResponse;
  /**
   * The className to be applied to the component
   */
  className?: string;
  /**
   * RSVP change call back function
   */
  onRsvpChange: (rsvpResponse: RsvpResponse) => void;
}

/**
 * Display rsvp response status for a published occasion.
 *
 * @author Vishnu Das
 */
const OccasionRsvpStatusForCurrentUser: React.FC<React.PropsWithChildren<Props>> = ({
  occasionId,
  rsvpStatus,
  className,
  onRsvpChange
}) => {
  const uidSeed = useUIDSeed();
  const cx = useClassNameMapper(localStyles);
  const { addToast } = useToasts();
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.OCCASION_RSVP_STATUS_FOR_CURRENT_USER
  );
  const { authUser } = useContext(AppContext);
  const [attendHover, setAttendHover] = useState<boolean>(false);
  const [interestedHover, setInterestedHover] = useState<boolean>(false);
  const uid = useId();
  const { router } = useEndUserRoutes();
  const { triggerAuthFlow } = useAuthFlow();
  const tenant = useContext(TenantContext);
  const {
    publicConfig: { multiAuthEnabled }
  } = tenant;
  const [frameEnd] = useFrameEnd();
  const client = useApolloClient();

  const { isAnonymous, isFullyRegistered } = useRegistrationStatus();

  const [anonymousUserAction, setAnonymousUserAction] = useLocalStorage<AnonymousUserAction>(
    LocalStorageKeys.ANONYMOUS_USER_ACTION_KEY,
    null
  );

  const [isRsvp, setIsRsvp] = useLocalStorage<RsvpResponse>(LocalStorageKeys.RSVP_RESPONSE, null);

  const [createRsvpResponse] = useMutationWithTracing<
    CreateRsvpResponseMutation,
    CreateRsvpResponseMutationVariables
  >(module, createRsvpResponseMutation);

  const [deleteRsvpResponse] = useMutationWithTracing<
    DeleteRsvpResponseMutation,
    DeleteRsvpResponseMutationVariables
  >(module, deleteRsvpResponseMutation);

  /**
   * Renders a success alert for rsvp actions.
   */
  const renderSuccessFeedback = useCallback(
    (rsvpResponse: RsvpResponse): void => {
      const toastProps: ToastProps = {
        toastVariant: ToastVariant.FLYOUT,
        alertVariant: ToastAlertVariant.SUCCESS,
        title: formatMessage(`toast.from.${rsvpStatus}.to.${rsvpResponse}.successTitle`),
        message: formatMessage(`toast.from.${rsvpStatus}.to.${rsvpResponse}.successDescription`),
        autohide: true,
        delay: 4000,
        id: `RsvpResponse-${uid}-success`
      };
      addToast(toastProps);
    },
    [addToast, formatMessage, uid, rsvpStatus]
  );

  /**
   * Renders failure toast messages
   * @param alertVariant The variant for the alert message
   * @param title Title to be displayed
   * @param body Toast body
   * @param autohide whether the toast should be hidden automatically after certain time interval
   */
  function renderToast(
    alertVariant: ToastAlertVariant,
    title: string,
    body: string,
    autohide = true
  ): void {
    const toastVariant =
      alertVariant === ToastAlertVariant.DANGER ? ToastVariant.BANNER : ToastVariant.FLYOUT;
    const toastProps: ToastProps = {
      id: uidSeed(title),
      toastVariant,
      alertVariant,
      title,
      autohide,
      message: body,
      delay: 4000
    };
    addToast(toastProps);
  }

  /**
   * Generates button text element based on the text and icon details provided.
   *
   * @param iconType icon type to be rendered.
   * @param text text value to be rendered.
   * @param iconSize size of the icon.
   * @param iconColor color of the icon.
   * @param isAttend true for attend button and false for interested button
   */
  function renderButtonText(
    iconType: IconTypes,
    text: string,
    iconSize: IconSize,
    iconColor: IconColor,
    isAttend: boolean
  ): React.ReactElement {
    return (
      <>
        {iconType && <Icon icon={iconType} size={iconSize} color={iconColor} />}
        <span
          data-testid={
            isAttend ? 'OccasionRsvpStatusAttendText' : 'OccasionRsvpStatusInterestedText'
          }
        >
          {text}
        </span>
      </>
    );
  }

  async function rsvp(rsvpResponse: RsvpResponse): Promise<void> {
    let fetchedResult;
    const parentFrameId = ParentFrameSupplier.getParentFrameId();
    if (rsvpResponse === RsvpResponse.No) {
      fetchedResult = await deleteRsvpResponse({
        variables: { occasionId: occasionId, userId: authUser.id },
        onCompleted: (d): void => {
          if (!hasErrors(d.deleteRsvpResponseForOccasion)) {
            frameEnd({
              context: { parentFrameId }
            });
          }
        }
      });
    } else {
      fetchedResult = await createRsvpResponse({
        variables: { occasionId: occasionId, rsvpResponse: rsvpResponse },
        refetchQueries: [
          {
            query: contextMessageQuery,
            variables: {
              id: occasionId
            }
          }
        ],
        onCompleted: (d): void => {
          if (!hasErrors(d.createRsvpResponseForOccasion)) {
            frameEnd({
              context: { parentFrameId }
            });
          }
        }
      });
    }

    const { data, errors } = await fetchedResult;
    const { result, errors: knownErrors } =
      rsvpResponse === RsvpResponse.No
        ? data?.deleteRsvpResponseForOccasion
        : data?.createRsvpResponseForOccasion;
    if (result) {
      onRsvpChange(rsvpResponse);
      renderSuccessFeedback(rsvpResponse);
      client.refetchQueries({
        updateCache(cache) {
          cache.evict({ fieldName: 'rsvps' });
        }
      });
    } else if (knownErrors) {
      if (knownErrors[0].__typename === 'RsvpOccasionValidationError') {
        renderToast(
          ToastAlertVariant.DANGER,
          formatMessage('rsvp.failed'),
          formatMessage(knownErrors[0].message)
        );
      }
    } else if (errors) {
      renderToast(
        ToastAlertVariant.DANGER,
        formatMessage('rsvp.failed'),
        formatMessage(errors[0].message)
      );
    }
    if (isRsvp) {
      setIsRsvp(null);
    }
  }

  /**
   * Handles the mutation call and response for rsvp.
   *
   * @param rsvpResponse response to be rsvp'd.
   */
  async function handleSubmit(rsvpResponse: RsvpResponse): Promise<void> {
    if (isAnonymous) {
      const userAction: AnonymousUserAction = {
        action: AnonAction.EVENT,
        context: { messageId: occasionId },
        timeStamp: Date.now()
      };

      setAnonymousUserAction(userAction);
      setIsRsvp(rsvpResponse);
      await triggerAuthFlow(
        () => {},
        multiAuthEnabled ? AuthFlow.MULTI_AUTH_LOGIN : AuthFlow.LOGIN,
        router.getCurrentRouteAndParams()
      );
    } else {
      rsvp(rsvpResponse);
    }
  }

  useEffect(() => {
    if (anonymousUserAction && isRsvp && isFullyRegistered) {
      rsvp(isRsvp);
      setAnonymousUserAction(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anonymousUserAction, isRsvp]);

  if (textLoading) {
    return null;
  }

  return (
    <div className={cx(className, 'lia-btn-wrap')} data-testid="OccasionRsvpStatusForCurrentUser">
      <Button
        size="lg"
        className={cx('lia-btn')}
        variant={ButtonVariant.PRIMARY}
        onClick={() =>
          handleSubmit(rsvpStatus === RsvpResponse.Yes ? RsvpResponse.No : RsvpResponse.Yes)
        }
        onMouseEnter={() => setAttendHover(true)}
        onMouseLeave={() => setAttendHover(false)}
        data-testid="OccasionRsvpStatusForCurrentUser.button.attend"
      >
        {renderButtonText(
          rsvpStatus === RsvpResponse.Yes
            ? attendHover
              ? null
              : Icons.CheckmarkSolidIcon
            : undefined,
          rsvpStatus === RsvpResponse.Yes
            ? attendHover
              ? formatMessage('Cancel')
              : formatMessage('Attending')
            : formatMessage('Attend'),
          IconSize.PX_16,
          IconColor.WHITE,
          true
        )}
      </Button>
      <Button
        className={cx('lia-btn')}
        variant={ButtonVariant.SECONDARY}
        disabled={rsvpStatus === RsvpResponse.Yes}
        onClick={() =>
          handleSubmit(rsvpStatus === RsvpResponse.Maybe ? RsvpResponse.No : RsvpResponse.Maybe)
        }
        onMouseEnter={() => setInterestedHover(true)}
        onMouseLeave={() => setInterestedHover(false)}
        data-testid="OccasionRsvpStatusForCurrentUser.button.interested"
      >
        {renderButtonText(
          rsvpStatus === RsvpResponse.Maybe
            ? interestedHover
              ? null
              : Icons.CheckmarkSolidIcon
            : undefined,
          rsvpStatus === RsvpResponse.Maybe && interestedHover
            ? formatMessage('Cancel')
            : formatMessage('Interested'),
          IconSize.PX_12,
          IconColor.BLACK,
          false
        )}
      </Button>
    </div>
  );
};

export default OccasionRsvpStatusForCurrentUser;
